import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiFetch, useApi, useAuth } from "@/lib/auth";
import { sanitizeRichText } from "@/lib/sanitize";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MapPin, User, Lock, Minus, Plus, ChevronLeft } from "lucide-react";
import { formatPrice } from "@/lib/format";

function Stepper({ value, min = 1, max = 10, onChange, testId }) {
  return (
    <div className="inline-flex items-center gap-2">
      <button type="button" onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        className="w-9 h-9 rounded-md border border-border grid place-items-center disabled:opacity-40 hover:bg-muted"
        data-testid={`${testId}-dec`}>
        <Minus size={16} />
      </button>
      <div className="min-w-[3.5rem] text-center font-semibold" data-testid={testId}>{value}</div>
      <button type="button" onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        className="w-9 h-9 rounded-md border border-border grid place-items-center disabled:opacity-40 hover:bg-muted"
        data-testid={`${testId}-inc`}>
        <Plus size={16} />
      </button>
    </div>
  );
}

function CompCodeInput({ value, onChange, state, testId }) {
  const inputRef = useRef(null);
  const chars = Array.from({ length: 6 }, (_, i) => value[i] || "");
  const focus = () => inputRef.current?.focus();
  const borderColor =
    state === "invalid" ? "border-destructive"
    : state === "valid" ? "border-emerald-600"
    : "border-border";
  const bg = state === "valid" ? "bg-emerald-50" : "bg-white";
  return (
    <div className="flex flex-col items-start">
      <div
        className="relative flex items-center gap-1 select-none cursor-text"
        onClick={focus}
        data-testid={`${testId}-row`}
      >
        {chars.map((ch, i) => (
          <div
            key={i}
            className={[
              "w-[28px] h-[38px] rounded border-2 grid place-items-center font-semibold text-primary",
              borderColor,
              bg,
              value.length === i && state !== "valid" ? "border-primary" : "",
            ].join(" ")}
          >
            {ch}
          </div>
        ))}
        <div className="ml-1 w-5 grid place-items-center">
          {state === "valid" && <span className="text-emerald-700 text-lg" aria-label="valid">✓</span>}
          {state === "invalid" && <span className="text-destructive text-lg" aria-label="invalid">✕</span>}
          {state === "validating" && <span className="text-muted-foreground text-xs" aria-label="checking">…</span>}
        </div>
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => {
            const cleaned = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
            onChange(cleaned);
          }}
          autoCapitalize="characters"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          maxLength={6}
          data-testid={testId}
          className="absolute inset-0 opacity-0 w-full h-full cursor-text"
          aria-label="Complimentary code"
        />
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        {state === "valid" ? "Complimentary — £0"
          : state === "invalid" ? <span className="text-destructive">Not valid for this event</span>
          : "6-character code from your organiser"}
      </div>
    </div>
  );
}

function CourseRow({ label, options, value, onChange, testIdPrefix, required }) {
  if (!options || options.length === 0) return null;
  return (
    <div>
      <Label>{label}{required && <span className="text-destructive"> *</span>}</Label>
      <div className="flex flex-wrap gap-2 mt-1">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={[
              "px-3 py-1.5 rounded-full text-sm border transition-colors",
              value === opt
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-white text-foreground border-border hover:bg-muted",
            ].join(" ")}
            data-testid={`${testIdPrefix}-${opt}`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function EventDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const api = useApi();
  const { token, user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [placesN, setPlacesN] = useState(1);
  const [customValues, setCustomValues] = useState({});
  // Prefill Attendee 1 from the signed-in member's profile — mirrors the
  // mobile behaviour. If the profile has a Masonic title, we prepend it to
  // the display name so the "W Bro John Smith" convention comes for free.
  const profileAttendee = () => {
    const t = (user?.masonic_title || "").trim();
    const nm = (user?.name || "").trim();
    return {
      name: t && nm ? `${t} ${nm}` : nm,
      grand_rank: user?.grand_rank || "",
      provincial_rank: user?.provincial_rank || "",
      dietary_concerns: user?.dietary_concerns || "",
      menu: {},
      custom_fields: {},
    };
  };
  const [attendees, setAttendees] = useState(() => [profileAttendee()]);
  const [hasCompCodes, setHasCompCodes] = useState(false);
  const [compCount, setCompCount] = useState(1);
  const [compEntries, setCompEntries] = useState([{ code: "", state: "idle" }]);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api(`/events/${id}`);
        const data = await r.json();
        if (r.ok) setEvent(data.event);
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [id]);

  // Sync attendee count with placesN — Attendee 1 keeps its profile
  // prefill; later attendees start blank.
  useEffect(() => {
    setAttendees((prev) => {
      const next = [...prev];
      while (next.length < placesN) next.push({ name: "", menu: {}, custom_fields: {} });
      return next.slice(0, placesN);
    });
    if (hasCompCodes && compCount > placesN) setCompCount(placesN);
  }, [placesN, hasCompCodes, compCount]);

  // Re-seed attendee[0] when the profile finishes hydrating (auth loads
  // asynchronously — /auth/me returns a moment after mount).
  useEffect(() => {
    setAttendees((prev) => {
      const first = prev[0] || {};
      const wasBlank = !first.name && !first.grand_rank && !first.provincial_rank && !first.dietary_concerns;
      if (!wasBlank) return prev;
      const seeded = profileAttendee();
      const isSeededEmpty = !seeded.name && !seeded.grand_rank && !seeded.provincial_rank && !seeded.dietary_concerns;
      if (isSeededEmpty) return prev;
      return [{ ...seeded, menu: first.menu || {}, custom_fields: first.custom_fields || {} }, ...prev.slice(1)];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.name, user?.masonic_title, user?.grand_rank, user?.provincial_rank, user?.dietary_concerns]);

  // Sync comp entries with compCount
  useEffect(() => {
    setCompEntries((prev) => {
      const next = [...prev];
      while (next.length < compCount) next.push({ code: "", state: "idle" });
      return next.slice(0, compCount);
    });
  }, [compCount]);

  const validateComp = useCallback(async (idx, code) => {
    if (!event) return;
    const dup = compEntries.some((e, i) => i !== idx && e.code === code && e.state === "valid");
    if (dup) {
      setCompEntries((prev) => { const n = [...prev]; n[idx] = { code, state: "invalid" }; return n; });
      return;
    }
    setCompEntries((prev) => { const n = [...prev]; n[idx] = { code, state: "validating" }; return n; });
    try {
      const r = await apiFetch("/bookings/validate-complimentary-code",
        { method: "POST", body: JSON.stringify({ event_id: event.event_id, code }) }, token);
      const data = await r.json();
      setCompEntries((prev) => {
        if ((prev[idx]?.code || "") !== code) return prev;
        const n = [...prev];
        n[idx] = r.ok && data.valid ? { code, state: "valid" } : { code, state: "invalid" };
        return n;
      });
    } catch {
      setCompEntries((prev) => { const n = [...prev]; n[idx] = { code, state: "invalid" }; return n; });
    }
  }, [event, token, compEntries]);

  const onCompChange = (idx, code) => {
    setCompEntries((prev) => {
      const n = [...prev];
      n[idx] = { code, state: code.length < 6 ? "idle" : "validating" };
      return n;
    });
    if (code.length === 6) void validateComp(idx, code);
  };

  const updateAttendee = (i, patch) => {
    setAttendees((prev) => {
      const n = [...prev];
      n[i] = { ...n[i], ...patch, menu: { ...(n[i]?.menu || {}), ...(patch.menu || {}) },
               custom_fields: { ...(n[i]?.custom_fields || {}), ...(patch.custom_fields || {}) } };
      return n;
    });
  };

  if (loading) return <div className="text-sm text-muted-foreground">Loading event…</div>;
  if (!event) return <div className="text-sm text-destructive">Event not found.</div>;

  const rawAmount = event.price_pence * placesN;
  const validComp = compEntries.slice(0, hasCompCodes ? compCount : 0).filter((e) => e.state === "valid").length;
  const discount = event.price_pence * validComp;
  const amountPence = Math.max(0, rawAmount - discount);
  const compEnabled = !!event.complimentary_codes_enabled;
  const bookingsClosed = !!(event.bookings_close_at && new Date(event.bookings_close_at).getTime() <= Date.now());
  const bookingScope = (event.custom_fields || []).filter((cf) => (cf.scope || "booking") !== "attendee");
  const attendeeScope = (event.custom_fields || []).filter((cf) => cf.scope === "attendee");

  // Menu config — mirrors mobile's menuConfigFromRaw().
  const rawMenu = event.menu_config || {};
  const menuCfg = {
    mode: rawMenu.mode || "none",
    meal_options: rawMenu.meal_options || [],
    starter_enabled: !!rawMenu.starter_enabled,
    starter_options: rawMenu.starter_options || [],
    main_options: rawMenu.main_options || [],
    dessert_enabled: !!rawMenu.dessert_enabled,
    dessert_options: rawMenu.dessert_options || [],
  };
  // Legacy fallback — if no menu_config but menu_choices is populated, treat it as simple.
  const effectiveMode = menuCfg.mode === "none" && (event.menu_choices || []).length > 0 ? "simple" : menuCfg.mode;
  const simpleOptions = menuCfg.mode === "simple" ? menuCfg.meal_options : (event.menu_choices || []);

  const onBook = async () => {
    setError(null);
    for (const cf of bookingScope) {
      if (cf.required && !(customValues[cf.name] || "").trim()) return setError(`Please fill: ${cf.name}`);
    }
    for (let i = 0; i < placesN; i++) {
      const a = attendees[i] || { name: "", menu: {}, custom_fields: {} };
      const tokens = (a.name || "").trim().split(/\s+/).filter(Boolean);
      if (tokens.length < 2) return setError(`Full name has not been provided on attendee ${i + 1}. Please enter both a first name and family name.`);
      for (const cf of attendeeScope) {
        if (cf.required && !((a.custom_fields || {})[cf.name] || "").trim()) return setError(`Please fill "${cf.name}" for attendee ${i + 1}.`);
      }
      if (effectiveMode === "simple" && simpleOptions.length > 0 && !a.menu?.meal) return setError(`Please pick a meal for attendee ${i + 1}.`);
      if (effectiveMode === "detailed") {
        if (menuCfg.starter_enabled && !a.menu?.starter) return setError(`Please pick a starter for attendee ${i + 1}.`);
        if (!a.menu?.main) return setError(`Please pick a main course for attendee ${i + 1}.`);
        if (menuCfg.dessert_enabled && !a.menu?.dessert) return setError(`Please pick a dessert for attendee ${i + 1}.`);
      }
    }
    setBooking(true);
    try {
      const compPayload = hasCompCodes
        ? compEntries.slice(0, compCount)
            .map((e, i) => (e.state === "valid" && e.code.length === 6 ? { attendee_index: i, code: e.code } : null))
            .filter(Boolean)
        : [];
      const r = await apiFetch("/bookings", {
        method: "POST",
        body: JSON.stringify({
          event_id: event.event_id,
          places: placesN,
          custom_field_values: customValues,
          attendees: attendees.slice(0, placesN),
          attendee_names: attendees.slice(0, placesN).map((a) => a.name),
          complimentary_codes: compPayload,
        }),
      }, token);
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Booking failed");
      if (data.requires_payment && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.success("Booking confirmed — we've emailed a confirmation.");
        nav(`/bookings/${data.booking.booking_id}`, { replace: true });
      }
    } catch (e) {
      setError(e.message || "Booking failed");
    } finally {
      setBooking(false);
    }
  };

  return (
    <div>
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm text-primary mb-4" data-testid="event-back">
        <ChevronLeft size={16} /> Back
      </button>
      <div className="text-[11px] uppercase tracking-widest text-destructive font-medium">
        {new Date(event.starts_at).toLocaleString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
      </div>
      <h1 className="text-3xl font-semibold mt-1" data-testid="event-name">{event.name}</h1>
      <div className="flex flex-wrap gap-2 mt-3">
        <Badge variant={event.price_pence === 0 ? "secondary" : "destructive"}>
          {formatPrice(event.price_pence, event.currency)}
        </Badge>
        <Badge variant="outline">{event.stripe_account_label}</Badge>
        {typeof event.places_remaining === "number" && event.capacity_visible !== false && (
          <Badge variant={event.places_remaining < 5 ? "destructive" : "secondary"} data-testid="event-capacity">
            {event.places_remaining} of {event.capacity} left
          </Badge>
        )}
      </div>

      <div className="grid gap-6 mt-6 md:grid-cols-3">
        {bookingsClosed ? (
          <Card className="p-5 border-destructive/50 bg-destructive/5">
            <div className="flex items-center gap-2 text-destructive font-semibold"><Lock size={18} /> Bookings closed</div>
            <p className="text-sm mt-2">
              Online bookings closed on{" "}
              <span className="font-medium">
                {event.bookings_close_at
                  ? new Date(event.bookings_close_at).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })
                  : "the deadline"}
              </span>. Please contact the organiser{event.organiser ? `, ${event.organiser}` : ""}{event.organiser_email ? ` (${event.organiser_email})` : ""}.
            </p>
            {event.organiser_email && (
              <a href={`mailto:${event.organiser_email}?subject=${encodeURIComponent("Late booking enquiry — " + event.name)}`}
                 className="inline-block mt-3">
                <Button data-testid="contact-organiser">Email {event.organiser || "organiser"}</Button>
              </a>
            )}
          </Card>
        ) : (
          <Card className="p-5">
            <h3 className="font-semibold mb-3">Make your booking</h3>
            {event.bookings_close_at && (
              <p className="text-xs text-muted-foreground mb-3">
                Bookings close on{" "}
                {new Date(event.bookings_close_at).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}.
              </p>
            )}
            <div className="space-y-1 mb-3">
              <Label>Number of places</Label>
              <Stepper value={placesN} min={1} max={event.max_places_per_booking} onChange={setPlacesN} testId="book-places" />
              <div className="text-xs text-muted-foreground">Up to {event.max_places_per_booking} per booking.</div>
            </div>

            {compEnabled && (
              <div className="border-t border-border pt-3 mt-3">
                <label className="flex items-center gap-2 text-sm" data-testid="book-comp-toggle">
                  <Checkbox checked={hasCompCodes} onCheckedChange={(v) => setHasCompCodes(!!v)} />
                  <span>I have complimentary booking codes</span>
                </label>
                {hasCompCodes && (
                  <div className="mt-3 space-y-1">
                    <Label>Number of complimentary codes</Label>
                    <Stepper value={compCount} min={1} max={placesN} onChange={setCompCount} testId="book-comp" />
                  </div>
                )}
              </div>
            )}

            <div className="border-t border-border pt-3 mt-3 space-y-2 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>{formatPrice(rawAmount, event.currency)}</span></div>
              {discount > 0 && <div className="flex justify-between text-emerald-700"><span>Complimentary codes</span><span>−{formatPrice(discount, event.currency)}</span></div>}
              <div className="flex justify-between font-semibold text-base border-t pt-2"><span>Total</span><span>{formatPrice(amountPence, event.currency)}</span></div>
            </div>
          </Card>
        )}

        <Card className="p-5 md:col-span-2">
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2"><MapPin size={16} className="text-primary" /> {event.location || "Location TBA"}</div>
            <div className="flex items-center gap-2"><User size={16} className="text-primary" /> {event.organiser || "Provincial team"}</div>
          </div>
          {event.description && (
            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">About</div>
              <div className="prose prose-sm max-w-none tiptap" dangerouslySetInnerHTML={{ __html: sanitizeRichText(event.description) }} />
            </div>
          )}
        </Card>
      </div>

      {!bookingsClosed && (
        <>
          {bookingScope.length > 0 && (
            <Card className="p-5 mt-6">
              <h3 className="font-semibold mb-3">Booking details</h3>
              {bookingScope.map((cf) => (
                <div key={cf.name} className="space-y-1 mb-3">
                  <Label>{cf.name}{cf.required && <span className="text-destructive"> *</span>}</Label>
                  <Input value={customValues[cf.name] || ""} onChange={(e) => setCustomValues((v) => ({ ...v, [cf.name]: e.target.value }))}
                         data-testid={`book-cf-${cf.name}`} />
                </div>
              ))}
            </Card>
          )}

          <Card className="p-5 mt-6">
            <h3 className="font-semibold mb-3">Attendees</h3>
            <div className="space-y-6">
              {Array.from({ length: placesN }).map((_, i) => (
                <div key={i} className="book-attendee p-4 rounded-md border-2 border-destructive/40 bg-destructive/[0.03]">
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Attendee {i + 1}</div>
                  {hasCompCodes && i < compCount && (
                    <div className="space-y-1 mb-3">
                      <Label>Complimentary code</Label>
                      <CompCodeInput
                        value={compEntries[i]?.code || ""}
                        state={compEntries[i]?.state || "idle"}
                        onChange={(c) => onCompChange(i, c)}
                        testId={`book-comp-code-${i}`}
                      />
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label>Full name{event.use_masonic_title ? " (with Masonic title)" : " (given + family)"} <span className="text-destructive">*</span></Label>
                    <Input value={attendees[i]?.name || ""} onChange={(e) => updateAttendee(i, { name: e.target.value })}
                           data-testid={`book-attendee-name-${i}`}
                           placeholder={event.use_masonic_title ? "W.Bro John Smith" : "John Smith"} />
                  </div>
                  {event.use_grand_rank && (
                    <div className="space-y-1 mt-3">
                      <Label>Grand rank</Label>
                      <Input value={attendees[i]?.grand_rank || ""}
                             onChange={(e) => updateAttendee(i, { grand_rank: e.target.value })}
                             placeholder="PJGD, PSGD, etc."
                             data-testid={`book-attendee-grand-${i}`} />
                    </div>
                  )}
                  {event.use_provincial_rank && (
                    <div className="space-y-1 mt-3">
                      <Label>Provincial rank</Label>
                      <Input value={attendees[i]?.provincial_rank || ""}
                             onChange={(e) => updateAttendee(i, { provincial_rank: e.target.value })}
                             placeholder="PrGStdB, PPrAGDC, etc."
                             data-testid={`book-attendee-prov-${i}`} />
                    </div>
                  )}
                  {event.use_dietary_concerns && (
                    <div className="space-y-1 mt-3">
                      <Label>Dietary concerns</Label>
                      <Textarea rows={2} maxLength={256} value={attendees[i]?.dietary_concerns || ""}
                                onChange={(e) => updateAttendee(i, { dietary_concerns: e.target.value.slice(0, 256) })}
                                placeholder="e.g. Vegetarian, nut allergy"
                                data-testid={`book-attendee-diet-${i}`} />
                    </div>
                  )}
                  {attendeeScope.map((cf) => (
                    <div key={cf.name} className="space-y-1 mt-3">
                      <Label>{cf.name}{cf.required && <span className="text-destructive"> *</span>}</Label>
                      <Input value={(attendees[i]?.custom_fields || {})[cf.name] || ""}
                             onChange={(e) => updateAttendee(i, { custom_fields: { [cf.name]: e.target.value } })}
                             placeholder={`Enter ${cf.name.toLowerCase()}`}
                             data-testid={`book-attendee-cf-${i}-${cf.name}`} />
                    </div>
                  ))}
                  {effectiveMode === "simple" && simpleOptions.length > 0 && (
                    <div className="space-y-1 mt-3">
                      <Label>Menu choice <span className="text-destructive">*</span></Label>
                      <div className="flex flex-wrap gap-2">
                        {simpleOptions.map((opt) => (
                          <button key={opt} type="button"
                            onClick={() => updateAttendee(i, { menu: { meal: opt } })}
                            className={[
                              "px-3 py-1.5 rounded-full text-sm border",
                              attendees[i]?.menu?.meal === opt
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-white text-foreground border-border hover:bg-muted",
                            ].join(" ")}
                            data-testid={`menu-${i}-${opt}`}>
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {effectiveMode === "detailed" && (
                    <div className="mt-3 space-y-3">
                      {menuCfg.starter_enabled && (
                        <CourseRow
                          label="Starter"
                          options={menuCfg.starter_options}
                          value={attendees[i]?.menu?.starter}
                          onChange={(v) => updateAttendee(i, { menu: { starter: v } })}
                          testIdPrefix={`menu-starter-${i}`}
                          required
                        />
                      )}
                      <CourseRow
                        label="Main course"
                        options={menuCfg.main_options}
                        value={attendees[i]?.menu?.main}
                        onChange={(v) => updateAttendee(i, { menu: { main: v } })}
                        testIdPrefix={`menu-main-${i}`}
                        required
                      />
                      {menuCfg.dessert_enabled && (
                        <CourseRow
                          label="Dessert"
                          options={menuCfg.dessert_options}
                          value={attendees[i]?.menu?.dessert}
                          onChange={(v) => updateAttendee(i, { menu: { dessert: v } })}
                          testIdPrefix={`menu-dessert-${i}`}
                          required
                        />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {error && <div className="mt-4 text-sm text-destructive" data-testid="book-error">{error}</div>}

          <div className="sticky bottom-4 mt-6 flex justify-end">
            <Button onClick={onBook} disabled={booking} data-testid="book-submit" className="min-w-[220px]">
              {booking ? "Please wait…" : amountPence > 0 ? `Pay ${formatPrice(amountPence, event.currency)}` : "Confirm free booking"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
